import { HttpStatusCode } from 'axios';
import isEqual from 'lodash/isEqual';
import { isApiError } from '@common/api/ApiError';
import httpClient from '@common/api/httpClient';
import httpNoAuthClient from '@common/api/httpNoAuthClient';
import { type Preference } from '@common/types/api';

export async function fetchGlobalPreference(
  key: string,
): Promise<string | undefined> {
  try {
    const { data } = await httpNoAuthClient.get<Preference>(`/rest/v1/preferences/global/${key}`);
    return data.value;
  } catch {
    return undefined;
  }
}

export async function fetchUserPreferenceSwallowingAllErrors<T>(
  key: string,
  signal?: AbortSignal,
) {
  try {
    const { data } = await httpClient.get<Preference>(`/rest/v1/preferences/user/${key}`, {
      signal,
    });
    return data.value ? JSON.parse(data.value) as T : undefined;
  } catch {
    return undefined;
  }
}

export async function fetchUserPreferenceThrowingServiceUnavailable<T>(
  key: string,
  signal?: AbortSignal,
) {
  try {
    const { data } = await httpClient.get<Preference>(`/rest/v1/preferences/user/${key}`, {
      signal,
    });
    return data.value ? JSON.parse(data.value) as T : undefined;
  } catch (err: unknown) {
    if (isApiError(err, 'server-error') && err.status === HttpStatusCode.ServiceUnavailable) {
      return Promise.reject(err);
    }
    return undefined;
  }
}

export async function addOrUpdateUserPreference<T>(
  key: string,
  value: T,
) {
  const preference = await fetchUserPreferenceSwallowingAllErrors<T>(key);
  if (isEqual(preference, value)) {
    return;
  }
  const stringValue = JSON.stringify(value);
  if (preference === undefined) {
    return httpClient.post<Preference>(
      '/rest/v1/preferences/user',
      { key, value: stringValue },
    );
  }
  return httpClient.put<Preference>(
    `/rest/v1/preferences/user/${key}`,
    { value: stringValue },
  );
}

export async function deleteUserPreference(
  key: string,
) {
  return httpClient.delete<Preference>(`/rest/v1/preferences/user/${key}`);
}
